<template>
  <div>
    <base-modal
      :showing="visible"
      size="max-w-5xl"
      @opened="onOpened"
      @close="$emit('close')"
    >
      <div class="space-y-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div>
              <h3 class="text-lg font-bold leading-6 text-gray-900">
                Daftar {{ title }}
              </h3>
              <p class="mt-1 max-w-2xl text-sm text-gray-500">
                Daftar {{ title }} Yang Telah Dibuat
              </p>
            </div>
          </div>
        </div>
        <div class="space-y-4 border-t border-gray-200 pt-6">
          <div class="flex justify-end gap-x-2">
            <base-input
              placeholder="Cari Kode atau Nama"
              type="search"
              :shadow="false"
              debounce
              v-model="filter.search"
              @native-input="loadOffices"
            />
          </div>
          <datatable
            :columns="tableColumns"
            :total="offices.meta.page.total"
            :perPage="offices.meta.page.perPage"
            :currentPage="offices.meta.page.currentPage"
            :meta="offices.meta"
            cursor
            @pagechanged="onPagechanged"
          >
            <template v-slot:tbody="{ classes }">
              <tr
                v-for="office in offices.data"
                :key="office.id"
                :class="[classes.tr, 'cursor-pointer hover:bg-green-100']"
                @click="onClickOffice(office)"
              >
                <td :class="[classes.td]">
                  <span class="font-bold text-gray-900">
                    {{ office.attributes.code }}
                  </span>
                  <template v-if="officeType === 'member'">
                    /
                    <span>
                      {{ office.attributes.temp_code }}
                    </span>
                  </template>
                </td>
                <td :class="[classes.td]">
                  {{ office.attributes.name }}
                </td>
                <td v-if="columns.includes('warehouse')" :class="[classes.td]">
                  {{
                    getSingleIncluded(
                      offices,
                      getSingleIncluded(
                        offices,
                        office.relationships.office.data.id
                      ).relationships['current-warehouse'].data.id
                    ).attributes.code
                  }}
                </td>
                <td v-if="officeType === 'member'" :class="[classes.td]">
                  {{ office.attributes.referred_by || '-' }}
                  /
                  {{ office.attributes.referred_by_name || '-' }}
                </td>
                <td
                  v-if="officeType !== 'member' && columns.includes('address')"
                  :class="[classes.td]"
                >
                  {{ office.attributes.address || '-' }}
                </td>
                <td
                  v-if="officeType !== 'member' && columns.includes('phone')"
                  :class="[classes.td]"
                >
                  {{ office.attributes.phone || '-' }}
                </td>
              </tr>
            </template>
          </datatable>
        </div>
      </div>
    </base-modal>
    <loading v-if="loading" />
  </div>
</template>

<script>
import BaseModal from '@/components/base/BaseModal';
import { requestMixin } from '@/mixins/request/request';
import { mapGetters } from 'vuex';

export default {
  components: {
    BaseModal,
  },
  mixins: [requestMixin],
  props: {
    visible: Boolean,
    requestParams: null,
    officeType: {
      type: String,
      default: 'stockist_center',
    },
    columns: {
      type: Array,
      default: () => ['address', 'phone'],
    },
    type: {
      type: String,
      default: 'office'
    }
  },
  emits: ['close', 'click-office'],
  data() {
    return {
      offices: {
        data: [],
        meta: {
          page: {},
        },
      },
      loading: false,
      filter: {
        search: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    tableColumns() {
      return [
        {
          id: 'code',
          name: `Kode ${this.title}`,
        },
        {
          id: 'name',
          name: `Nama ${this.title}`,
        },
        ...(this.columns.includes('warehouse')
          ? [
              {
                id: 'warehouse',
                name: 'Kode Gudang',
              },
            ]
          : []),
        ...(this.officeType === 'member'
          ? [
              {
                id: 'upline',
                name: 'Upline',
              },
            ]
          : [
              ...(this.columns.includes('address')
                ? [
                    {
                      id: 'address',
                      name: 'Alamat',
                    },
                  ]
                : []),
              ...(this.columns.includes('phone')
                ? [
                    {
                      id: 'phone',
                      name: 'No. Telp',
                    },
                  ]
                : []),
            ]),
      ];
    },
    title() {
      return {
        stockist_center: 'Stockist Center',
        supplier: 'Supplier',
        kantor_pelayanan: 'Kantor Pelayanan',
        pusat: 'Pusat',
        stockist: 'Stockist',
        member: 'Mitra Usaha',
        all: 'Kantor',
      }[this.officeType];
    },
    officeCategoryId() {
      return {
        stockist_center: 3,
        supplier: 6,
        pusat: 1,
        kantor_pelayanan: 2,
        stockist: 4,
        member: 5,
        all: null,
      }[this.officeType];
    },
  },
  methods: {
    async loadOffices(params) {
      this.loading = true;

      const [res, error] = await this.request(this.type === 'downline' ? `/api/v1/offices/${this.me.office_id}/simple-office-downlines` : `/api/v1/offices`, {
        params: {
          ...this.requestParams,
          'page[limit]': 5,
          'fields[simple-offices]': [
            'code',
            'temp_code',
            'name',
            'office_id',
            'referred_by',
            'referred_by_name',
            ...(this.columns.includes('warehouse') ? ['office'] : []),
          ].join(','),
          ...(this.columns.includes('warehouse')
            ? { 'fields[offices]': 'current-warehouse' }
            : {}),
          ...(this.columns.includes('warehouse')
            ? { 'fields[warehouses]': 'code' }
            : {}),
          'filter[search]': this.filter.search,
          'filter[office_category_id]': this.officeCategoryId,
          ...(this.officeType === 'all' ? { 'filter[is_active]': true } : {}),
          ...(this.columns.includes('warehouse')
            ? { include: 'office.current-warehouse' }
            : {}),
          ...params,
        },
      });

      if (!error) {
        this.offices = res;
      }

      this.loading = false;
    },
    onClickOffice(office) {
      this.$emit('click-office', office, this.offices);
    },
    onOpened() {
      this.filter.search = null;

      this.loadOffices();
    },
    onPagechanged(page) {
      this.loadOffices({
        ...page,
      });
    },
  },
};
</script>
